import type { InboundConsignment, SupplierPayment } from '../data/endpoints'
import type { ResourceState } from '../data/resource'
import { AsyncPanel } from '../ui/AsyncPanel'
import { formatPaise } from '@tkm/money'

/**
 * Jayant's screens. Four labels, four different screens.
 *
 * They were four labels and one screen, headed "Approvals" every time, listing
 * shipment retries. He is retired and new to software: a navigation where four
 * different words produce one identical page is not confusing, it is the moment
 * he decides the app is broken. And his actual job - supplier payments and
 * inbound - had no screen at all.
 *
 * Everything here is deliberately the plainest thing in the product: one list,
 * one number per row, one verb. No nesting, no tabs, no filters.
 */

export function SupplierPayments({
  state,
  onRetry,
  onPay,
  confirming,
}: {
  state: ResourceState<{ items: SupplierPayment[] }>
  onRetry: () => void
  onPay?: (id: string) => void
  /** The id currently awaiting its second tap, per the deliberate two-tap confirm. */
  confirming?: string | null
}) {
  return (
    <main className="shell admin">
      <h1 className="t-display">Supplier payments</h1>
      <AsyncPanel state={state} emptyMessage="No payments due - nothing to pay today." onRetry={onRetry}>
        {(data) => (
          <ul className="admin-list">
            {data.items.map((p) => (
              <li key={p.id} className="admin-row">
                <span className="admin-row-name">{p.supplierName ?? 'Supplier'}</span>
                <span className="admin-row-amount">{formatPaise(p.amountPaise)}</span>
                <span className="admin-row-meta">
                  {p.dueDate ? `Due ${p.dueDate}` : 'No due date'} · {p.method}
                </span>
                {/* Two taps by design. ui-design-system s8: "slow is fine, wrong
                    is not." The first tap changes the word, the second one acts. */}
                <button
                  type="button"
                  className={`btn admin-verb${confirming === p.id ? ' confirming' : ''}`}
                  onClick={() => onPay?.(p.id)}
                >
                  {confirming === p.id ? 'Tap again to confirm' : 'Mark paid'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </AsyncPanel>
    </main>
  )
}

export function Inbound({
  state,
  onRetry,
}: {
  state: ResourceState<{ items: InboundConsignment[] }>
  onRetry: () => void
}) {
  return (
    <main className="shell admin">
      <h1 className="t-display">Coming in</h1>
      <AsyncPanel state={state} emptyMessage="Nothing expected - no consignments on the way." onRetry={onRetry}>
        {(data) => (
          <ul className="admin-list">
            {data.items.map((c) => (
              <li key={c.id} className="admin-row">
                <span className="admin-row-name">{c.supplierName ?? 'Supplier'}</span>
                <span className="admin-row-meta">
                  {c.expectedDate ? `Expected ${c.expectedDate}` : 'No date given'} · {c.status}
                  {c.transporter ? ` · ${c.transporter}` : ''}
                </span>
                {c.declaredAmountPaise ? (
                  <span className="admin-row-amount">{formatPaise(c.declaredAmountPaise)}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </AsyncPanel>
    </main>
  )
}

/**
 * Today. Not a dashboard - the two or three facts that answer "did today work",
 * and nothing that needs interpreting. Counts come from the same home-queue read
 * the home screen uses, so the two screens can never disagree about the number.
 */
export function Today({
  counts,
  onRetry,
  state,
}: {
  counts: Array<{ label: string; value: string }>
  onRetry: () => void
  state: ResourceState<unknown>
}) {
  return (
    <main className="shell admin">
      <h1 className="t-display">Today</h1>
      <AsyncPanel state={state} emptyMessage="Nothing has happened yet today." onRetry={onRetry}>
        {() => (
          <dl className="admin-facts">
            {counts.map((c) => (
              <div key={c.label} className="admin-fact">
                <dt>{c.label}</dt>
                <dd>{c.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </AsyncPanel>
    </main>
  )
}
